import React, { useRef, useState } from "react"
import { JSONSchema7 } from "json-schema"
import Form from "@rjsf/core"
import { SchemaForm } from "resha"

import { useUrlSignerFromContext } from "../../../../Hooks/useUrlSigner"
import { SchemaFormModal } from "../Modals"
import { FieldParameter, TextArrayField } from "../Fields"
import { TextWidget } from "../Widgets"

export type DocumentType = {
    advices: string[]
    instruction: string
    name: string
}

export enum DocumentModalType {
    New = "new",
    Edit = "edit"
}

export type DocumentModalOptions = {
    index?: number
    type?: DocumentModalType
}

type DocumentModalProps = {
    onClose: () => void
    onSubmit: (newDocument: DocumentType) => void
    schema: JSONSchema7
    index: number
    value?: DocumentType
    type?: DocumentModalType
}

export const DocumentModal = ({
    onClose,
    onSubmit,
    schema,
    value,
    type = DocumentModalType.New
}: DocumentModalProps) => {
    const urlSignerFromContext = useUrlSignerFromContext()

    const formRef = useRef<Form<unknown>>(null)

    const documentSchema = schema.definitions?.DocumentLLMParams as JSONSchema7

    const [state, setState] = useState<DocumentType | undefined>(value)

    const triggerSubmit = () => {
        formRef.current?.submit()
    }

    const handleChange = (data: unknown) => {
        setState(data as DocumentType)
    }

    const handleSubmit = (data: unknown) => {
        onSubmit(data as DocumentType)
        onClose()
    }

    return (
        <SchemaFormModal
            testId="document-modal"
            title={
                type === DocumentModalType.Edit
                    ? "Edit document"
                    : "Add new document"
            }
            onClose={onClose}
            onSubmit={triggerSubmit}
            submit={{
                label:
                    type === DocumentModalType.Edit
                        ? "Update document"
                        : "Add document"
            }}
        >
            <SchemaForm
                ref={formRef}
                schema={documentSchema}
                formData={state}
                uiSchema={{
                    name: {
                        "ui:field": FieldParameter,
                        "ui:widget": TextWidget
                    },
                    instruction: {
                        "ui:field": FieldParameter,
                        "ui:widget": TextWidget
                    },
                    advices: {
                        "ui:field": TextArrayField,
                        "ui:options": {
                            addButtonLabel: "Add advice"
                        }
                    }
                }}
                onSubmit={handleSubmit}
                onChange={handleChange}
                enableImageTools={false}
                skipMissingDataField={false}
                urlSigner={urlSignerFromContext}
            />
        </SchemaFormModal>
    )
}
